import React, {
  DetailedHTMLProps,
  ButtonHTMLAttributes,
  forwardRef,
} from 'react';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import classnames from 'classnames';
import { ChevronRightLgIcon as ChevronRightIcon } from '@stpkg/stand-react';

export type ResourceContentButtonProps = Omit<
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  'children'
> & {
  name: string;
  thumbnailUrl?: string;
};

export const ResourceContentButton = forwardRef<
  HTMLButtonElement,
  ResourceContentButtonProps
>(({ name, thumbnailUrl, ...props }, ref) => {
  const { isFocusVisible, focusProps } = useFocusRing();

  const color = '#0066FF';

  return (
    <>
      <div
        className={classnames(
          'wrapper',
          'h-[67px]',
          'relative',
          isFocusVisible && 'focus-visible',
          'after:transition',
        )}
      >
        <button
          {...mergeProps(props, focusProps)}
          ref={ref}
          className="z-1 pc:px-4 group relative box-border block h-full w-full cursor-pointer border-none bg-transparent px-2.5 py-1.5 text-left outline-none focus:outline-none"
        >
          <div className="flex items-center justify-between">
            <div className="flex flex-auto items-center">
              {thumbnailUrl && (
                <div className="mr-1.5 flex-none">
                  <img
                    className="rounded-10 box-border block h-44 w-44 border border-solid border-gray-50 object-cover object-center"
                    src={thumbnailUrl}
                    alt=""
                  />
                </div>
              )}

              <div className="flex-auto">
                <div className="rs-font text-13 text-gray-1000 pc:text-12 line-clamp-2 text-ellipsis font-normal transition group-hover:text-gray-400">
                  {name}
                </div>
              </div>
            </div>

            <div className="ml-1 flex-none">
              <ChevronRightIcon
                width="16"
                height="16"
                viewBox="0 0 48 48"
                className="block fill-gray-400 transition group-hover:fill-gray-200"
              />
            </div>
          </div>
        </button>
      </div>
      <style jsx>{`
        .wrapper::after {
          content: '';
          box-sizing: border-box;
          position: absolute;
          width: 100%;
          height: calc(100% + 1px);
          top: 0;
          left: 0;
          border-style: solid;
          border-width: 2px;
          border-color: transparent;
        }

        .wrapper.focus-visible::after {
          border-color: ${color};
        }
      `}</style>
    </>
  );
});

ResourceContentButton.displayName = 'PageContentButton';
